import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';
import { routes } from 'utils/constants/routes';
import { EventMap } from '@redislabsdev/cloud-ui-analytics';
import { logout } from '../store/auth/auth.action';
import useClearDbTablePersistence from '../screens/SubscriptionsAndDatabases/Databases/components/DatabasesMain/components/DatabasesTable/hooks/useClearDbTablePersistence';
import { useFeatureFlags } from '../components/FeatureFlagsProvider/hooks/useFeatureFlags';
import { closeOktaSession } from '../utils/oktaInstance';

const useLogOut = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const dispatch = useDispatch();
  const { isAuthenticated: isOktaAuthenticated } = authState || {};
  const { clearTablePersistence } = useClearDbTablePersistence();
  const {
    flags: { enableKeepOktaSession }
  } = useFeatureFlags();

  const performLogout = useCallback(
    async ({
      url = '',
      logoutType = 'user'
    }: {
      url?: string;
      logoutType?: EventMap['LoggedOut']['logout_type'];
    } = {}) => {
      const localOktaTokens = await oktaAuth.tokenManager.getTokens();
      const localTokensLength = Object.keys(localOktaTokens).length;

      oktaAuth?.authStateManager.unsubscribe();

      clearTablePersistence();

      if (isOktaAuthenticated || localTokensLength) {
        if (enableKeepOktaSession) {
          // Flag ON: Clear local tokens only - keep Okta server session active
          // This allows the same user to quickly re-authenticate
          const samlLogoutUrl = window.sessionStorage.getItem('samlLogoutUrl');

          if (samlLogoutUrl) {
            // SAML user with logout URL - close Okta session for consistency
            // since they'll be redirected to their IDP logout page
            await closeOktaSession();
          } else {
            // No SAML logout URL - just clear local tokens, keep Okta session active
            try {
              oktaAuth.tokenManager.clear();
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[performLogout] Failed to clear tokens:', error);
            }
          }
        } else {
          // Flag OFF: Original behavior - full Okta sign out
          if (url !== '') {
            window.sessionStorage.setItem('oktaLogOutRedirect', encodeURIComponent(url));
          }

          await oktaAuth.signOut({
            clearTokensBeforeRedirect: true
          });

          return;
        }
      }

      dispatch(
        logout({
          redirect: url !== '' ? url : routes.login,
          logoutType
        })
      );
    },
    [oktaAuth, isOktaAuthenticated, clearTablePersistence, dispatch, enableKeepOktaSession]
  );

  return { performLogout, isOktaAuthenticated };
};

export default useLogOut;
